import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { ErrorNotice, useAction } from "../components/ui";
import { api, type Me, type PrivacySettings } from "../lib/api";
import { noticeUrl } from "../lib/notice";

const pad = (n: number) => String(n).padStart(2, "0");
const dateFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });

/** The agency's data protection controls: retention period, review list, accepted terms. */
export function Privacy({ me }: { me: Me }) {
  const isOwner = me.user.role === "owner";
  const [settings, setSettings] = useState<PrivacySettings | null>(null);
  const [due, setDue] = useState<{ id: string; fullName: string; status: string; lastActivity: string }[] | null>(null);
  const [months, setMonths] = useState("");
  const [confirmErase, setConfirmErase] = useState<string | null>(null);
  const load = useAction();
  const act = useAction();

  const reload = () =>
    load.run(async () => {
      const [s, d] = await Promise.all([api.privacy(), api.retentionDue()]);
      setSettings(s);
      setMonths(String(s.retentionMonths));
      setDue(d.candidates);
    });
  useEffect(() => void reload(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMonths = (e: FormEvent) => {
    e.preventDefault();
    void act.run(async () => setSettings(await api.updatePrivacy({ retentionMonths: Number(months) })));
  };

  return (
    <>
      <div className="page-head">
        <p className="label">{me.tenant.name}</p>
        <h1 className="display">
          Keep only what you <em>need</em>.
        </h1>
        <p className="lede">
          You're the controller of your candidates' and contacts' data. Review people you haven't worked with in a while, and keep or erase
          them.
        </p>
      </div>
      <ErrorNotice error={load.error ?? act.error} />

      {settings && !settings.legalUpToDate && (
        <p className="notice">
          {isOwner ? (
            <>
              Please review and accept the <Link to="/legal/terms">terms of service</Link>,{" "}
              <Link to="/legal/dpa">data processing agreement</Link> and <Link to="/legal/privacy">privacy policy</Link> (version{" "}
              {settings.currentLegalVersion}).{" "}
              <button
                type="button"
                className="link-btn"
                disabled={act.pending}
                onClick={() => void act.run(async () => setSettings(await api.acceptLegal(settings.currentLegalVersion)))}
              >
                Accept on behalf of {me.tenant.name}
              </button>
            </>
          ) : (
            "An owner of your company needs to accept the updated terms of service, data processing agreement and privacy policy."
          )}
        </p>
      )}

      {settings && <NoticeSettings me={me} settings={settings} onSaved={setSettings} />}

      <section className="section" aria-labelledby="ret-h">
        <div className="section-head">
          <h2 id="ret-h" className="label">
            Retention period
          </h2>
        </div>
        <form className="button-row spaced-top" onSubmit={saveMonths}>
          <label className="field compact">
            <span className="label">Review candidates after this many months without activity</span>
            <input inputMode="numeric" value={months} onChange={(e) => setMonths(e.target.value)} disabled={!isOwner} />
          </label>
          {isOwner && (
            <button className="btn secondary" disabled={act.pending || months === String(settings?.retentionMonths)}>
              Save
            </button>
          )}
        </form>
      </section>

      <section className="section" aria-labelledby="due-h">
        <div className="section-head">
          <h2 id="due-h" className="label">
            Due for review
          </h2>
          <span className="label figures">{due ? pad(due.length) : "…"}</span>
        </div>
        <ol className="index">
          {due?.map((c, i) => (
            <li key={c.id}>
              <span className="num" aria-hidden>
                {pad(i + 1)}
              </span>
              <span className="main">
                <Link className="primary" to={`/candidates/${c.id}`}>
                  {c.fullName}
                </Link>
                <span className="secondary">
                  last activity <span className="figures">{dateFmt.format(new Date(c.lastActivity))}</span> · {c.status}
                </span>
              </span>
              <span className="actions">
                {confirmErase === c.id ? (
                  <>
                    <span className="label alert">Erase {c.fullName}?</span>
                    <button
                      type="button"
                      className="link-btn danger"
                      disabled={act.pending}
                      onClick={() =>
                        void act.run(async () => {
                          await api.eraseCandidate(c.id);
                          setConfirmErase(null);
                          await reload();
                        })
                      }
                    >
                      Erase permanently
                    </button>
                    <button type="button" className="link-btn" onClick={() => setConfirmErase(null)}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="link-btn"
                      disabled={act.pending}
                      onClick={() =>
                        void act.run(async () => {
                          await api.keepCandidate(c.id);
                          await reload();
                        })
                      }
                    >
                      Keep
                    </button>
                    {isOwner && (
                      <button type="button" className="link-btn danger" onClick={() => setConfirmErase(c.id)}>
                        Erase
                      </button>
                    )}
                  </>
                )}
              </span>
            </li>
          ))}
          {due && due.length === 0 && (
            <li>
              <span className="empty">Nobody is due for review.</span>
            </li>
          )}
        </ol>
      </section>

      {settings?.legalAcceptedAt && (
        <p className="hint spaced-top">
          <Link to="/legal/privacy">Privacy policy</Link> and <Link to="/legal/dpa">data processing agreement</Link> version{" "}
          <span className="figures">{settings.legalAcceptedVersion}</span> accepted {dateFmt.format(new Date(settings.legalAcceptedAt))}. Also see the{" "}
          <Link to="/legal/terms">terms of service</Link>.
        </p>
      )}
    </>
  );
}

/** The candidate privacy notice: a public page per agency, filled in from these details. */
function NoticeSettings({ me, settings, onSaved }: { me: Me; settings: PrivacySettings; onSaved: (s: PrivacySettings) => void }) {
  const isOwner = me.user.role === "owner";
  const [contact, setContact] = useState(settings.noticeContact ?? "");
  const [ico, setIco] = useState(settings.icoNumber ?? "");
  const [copied, setCopied] = useState(false);
  const { pending, error, run } = useAction();
  const url = noticeUrl(me.tenant.id);
  const dirty = contact.trim() !== (settings.noticeContact ?? "") || ico.replace(/\s/g, "").toUpperCase() !== (settings.icoNumber ?? "");
  const save = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const saved = await api.updatePrivacy({ noticeContact: contact, icoNumber: ico });
      onSaved(saved);
      setContact(saved.noticeContact ?? "");
      setIco(saved.icoNumber ?? "");
    });
  };
  return (
    <section className="section" aria-labelledby="notice-h">
      <div className="section-head">
        <h2 id="notice-h" className="label">
          Privacy notice for candidates
        </h2>
      </div>
      <p>
        When you add someone, you have a month to tell them you hold their details. Your agency has a ready-made notice at{" "}
        <a href={url} target="_blank" rel="noopener">
          {url.replace(/^https?:\/\//, "")}
        </a>
        . Today lists who's owed one, with an email that links to it.
      </p>
      <div className="button-row">
        <button type="button" className="btn secondary" onClick={() => void navigator.clipboard.writeText(url).then(() => setCopied(true))}>
          Copy the link
        </button>
        {copied && (
          <span className="hint" role="status">
            Copied. Put it in your email signature too.
          </span>
        )}
      </div>
      {!settings.noticeContact && <p className="notice">Add where candidates can contact you about their data. Until then the notice tells them to reply to the recruiter.</p>}
      <form className="form spaced-top" onSubmit={save}>
        <div className="grid-2">
          <label className="field">
            <span className="label">Contact for data questions</span>
            <input value={contact} maxLength={500} onChange={(e) => setContact(e.target.value)} disabled={!isOwner} placeholder="e.g. privacy@youragency.co.uk" />
          </label>
          <label className="field">
            <span className="label">ICO registration number</span>
            <input value={ico} maxLength={20} onChange={(e) => setIco(e.target.value)} disabled={!isOwner} placeholder="e.g. ZA123456" />
          </label>
        </div>
        <p className="hint">
          Most recruitment agencies must pay the ICO data protection fee and get a number: <a href="https://ico.org.uk/for-organisations/data-protection-fee/" target="_blank" rel="noopener">check at ico.org.uk</a>. The notice is a starting point written for agencies like yours, not legal advice: read it and check it matches how you work.
        </p>
        <ErrorNotice error={error} />
        {isOwner && (
          <div className="button-row">
            <button className="btn secondary" disabled={pending || !dirty}>
              {pending ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </form>
    </section>
  );
}
